/*
 * adaptive-video.js — connection-aware video loading (drop-in, self-contained)
 *
 * Goal: make the site render fast on slow internet by never blocking on heavy
 * autoplay video downloads. Instead of "wait for the full clip", we:
 *   - lazy-load decorative autoplay videos so off-screen clips don't download up front
 *     (huge win even on good connections — every autoplay bg currently downloads at once);
 *   - on SLOW connections, don't autoplay at all — show the still frame and let the
 *     user tap to play;
 *   - on DATA-SAVER (Save-Data / prefers-reduced-data), download nothing until tapped.
 *
 * Scope: only decorative `video[autoplay]`. Interactive/tab-controlled videos
 * (`[data-tab]`, `.tab-media`) and anything marked `[data-av-skip]` are left alone,
 * as are the Shopify theme editor (design mode) and reduced-motion is honoured.
 *
 * No section markup changes required; loaded once from layout/theme.liquid.
 */
(function (root, factory) {
  var api = factory();
  // Browser: auto-run. Node (tests): export the pure core.
  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  } else if (root && root.document) {
    api.boot(root);
  }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var MANAGED_SELECTOR = 'video[autoplay]';
  var EXCLUDE_SELECTOR = '[data-tab], .tab-media, [data-av-skip]';
  var ROOT_MARGIN = '400px'; // start loading a bit before the video scrolls in

  /* ---------------------------------------------------------------------------
   * Pure helpers — no DOM globals, unit-tested in node.
   * ------------------------------------------------------------------------- */

  function isSlowEffectiveType(et) {
    return et === 'slow-2g' || et === '2g' || et === '3g';
  }

  // Strong signal the user opted into saving data → download nothing until tapped.
  function isDataSaver(conn, prefersReducedData) {
    if (prefersReducedData) return true;
    return !!(conn && conn.saveData === true);
  }

  // Slow link (or data-saver, or reduced motion) → don't autoplay.
  function isSlowConnection(conn, prefersReducedData, prefersReducedMotion) {
    if (isDataSaver(conn, prefersReducedData)) return true;
    if (prefersReducedMotion) return true;
    if (!conn) return false;
    if (isSlowEffectiveType(conn.effectiveType)) return true;
    if (typeof conn.downlink === 'number' && conn.downlink > 0 && conn.downlink < 1.5) return true;
    return false;
  }

  // Decide what to do with a managed video given the environment.
  //   'lazy'   — park + activate when it nears the viewport (fast connections)
  //   'tap'    — park (keep src so the first frame can show), play on tap (slow)
  //   'unload' — park + detach sources, load on tap (data saver)
  function decideMode(env) {
    if (isDataSaver(env.connection, env.prefersReducedData)) return 'unload';
    if (isSlowConnection(env.connection, env.prefersReducedData, env.prefersReducedMotion)) return 'tap';
    return 'lazy';
  }

  /* ---------------------------------------------------------------------------
   * Element ops — DOM-method based, so a fake element makes them testable.
   * ------------------------------------------------------------------------- */

  // Stop a video from auto-downloading. `detach` removes the actual media URLs so
  // not a single byte is fetched; otherwise we keep them at preload=metadata so the
  // browser can paint the first frame cheaply.
  function park(video, detach) {
    if (video.getAttribute('data-av') === 'parked') return video;

    video.setAttribute('data-av-autoplay', video.hasAttribute('autoplay') ? '1' : '0');
    video.setAttribute('data-av-detached', detach ? '1' : '0');
    if (!video.hasAttribute('data-av-preload')) {
      video.setAttribute('data-av-preload', video.getAttribute('preload') || '');
    }

    video.removeAttribute('autoplay');
    if (typeof video.autoplay === 'boolean') video.autoplay = false;

    if (detach) {
      if (video.hasAttribute('src')) {
        video.setAttribute('data-av-src', video.getAttribute('src'));
        video.removeAttribute('src');
      }
      var sources = video.querySelectorAll ? video.querySelectorAll('source') : [];
      for (var i = 0; i < sources.length; i++) {
        var s = sources[i];
        if (s.hasAttribute('src')) {
          s.setAttribute('data-av-src', s.getAttribute('src'));
          s.removeAttribute('src');
        }
      }
      video.setAttribute('preload', 'none');
      if (typeof video.load === 'function') video.load(); // abort any in-flight fetch
    } else {
      video.setAttribute('preload', 'metadata');
    }

    video.setAttribute('data-av', 'parked');
    return video;
  }

  // Restore a parked video and (if it was an autoplay clip) start playing.
  function activate(video) {
    if (video.getAttribute('data-av') === 'active') return video;

    if (video.getAttribute('data-av-detached') === '1') {
      if (video.hasAttribute('data-av-src')) {
        video.setAttribute('src', video.getAttribute('data-av-src'));
        video.removeAttribute('data-av-src');
      }
      var sources = video.querySelectorAll ? video.querySelectorAll('source') : [];
      for (var i = 0; i < sources.length; i++) {
        var s = sources[i];
        if (s.hasAttribute('data-av-src')) {
          s.setAttribute('src', s.getAttribute('data-av-src'));
          s.removeAttribute('data-av-src');
        }
      }
    }

    video.setAttribute('preload', 'metadata');
    if (typeof video.load === 'function') video.load();

    var wantsPlay = video.getAttribute('data-av-autoplay') === '1';
    video.setAttribute('data-av', 'active');

    if (wantsPlay && typeof video.play === 'function') {
      var p = video.play();
      if (p && typeof p.catch === 'function') p.catch(function () {});
    }
    return video;
  }

  /* ---------------------------------------------------------------------------
   * Browser wiring.
   * ------------------------------------------------------------------------- */

  function matches(el, selector) {
    if (!el || el.nodeType !== 1) return false;
    var fn = el.matches || el.webkitMatchesSelector || el.msMatchesSelector;
    return fn ? fn.call(el, selector) : false;
  }

  function isManaged(el) {
    return matches(el, MANAGED_SELECTOR) && !matches(el, EXCLUDE_SELECTOR);
  }

  function readEnv(win) {
    var nav = win.navigator || {};
    var mm = win.matchMedia || function () { return { matches: false }; };
    return {
      connection: nav.connection || nav.mozConnection || nav.webkitConnection || null,
      prefersReducedData: mm.call(win, '(prefers-reduced-data: reduce)').matches,
      prefersReducedMotion: mm.call(win, '(prefers-reduced-motion: reduce)').matches
    };
  }

  function boot(win) {
    var doc = win.document;

    // Never interfere with the Shopify theme editor.
    if (win.Shopify && win.Shopify.designMode) return;

    var env = readEnv(win);
    var mode = decideMode(env); // 'lazy' | 'tap' | 'unload'
    var detach = mode !== 'tap'; // 'lazy' and 'unload' detach; 'tap' keeps src for the still frame

    var parked = [];

    function handle(video) {
      if (!isManaged(video) || video.getAttribute('data-av')) return;
      park(video, detach);
      parked.push(video);
      if (mode !== 'lazy') wireTap(video);
    }

    function wireTap(video) {
      video.style.cursor = 'pointer';
      video.setAttribute('data-av-tap', '1');
      video.addEventListener('click', function onTap() {
        video.removeEventListener('click', onTap);
        video.style.cursor = '';
        video.removeAttribute('data-av-tap');
        activate(video);
      });
    }

    // 1) Intercept videos as the page parses so heavy autoplay clips never start
    //    downloading. Installed immediately (this script is in <head>, not deferred).
    var mo = null;
    if (win.MutationObserver) {
      mo = new win.MutationObserver(function (mutations) {
        for (var m = 0; m < mutations.length; m++) {
          var added = mutations[m].addedNodes;
          for (var n = 0; n < added.length; n++) {
            var node = added[n];
            if (node.nodeType !== 1) continue;
            if (node.tagName === 'VIDEO') handle(node);
            else if (node.querySelectorAll) {
              var vids = node.querySelectorAll('video[autoplay]');
              for (var v = 0; v < vids.length; v++) handle(vids[v]);
            }
          }
        }
      });
      mo.observe(doc.documentElement, { childList: true, subtree: true });
    }

    function onReady() {
      if (mo) mo.disconnect();
      // Final sweep for anything the observer missed (or if it was unavailable).
      var all = doc.querySelectorAll(MANAGED_SELECTOR);
      for (var i = 0; i < all.length; i++) handle(all[i]);

      if (mode === 'lazy') setupLazy();
      else if (mode === 'unload') showDataSaverHint();
    }

    function setupLazy() {
      if (!win.IntersectionObserver) {
        // No IO support → just restore everything (still better than nothing).
        for (var i = 0; i < parked.length; i++) activate(parked[i]);
        return;
      }
      var io = new win.IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            activate(entries[i].target);
            io.unobserve(entries[i].target);
          }
        }
      }, { rootMargin: ROOT_MARGIN });
      for (var j = 0; j < parked.length; j++) io.observe(parked[j]);
    }

    var hintShown = false;
    function showDataSaverHint() {
      if (hintShown || !parked.length) return;
      hintShown = true;
      var bar = doc.createElement('div');
      bar.setAttribute('data-av-hint', '1');
      bar.textContent = 'Data saver on — videos are paused. Tap a video to play it.';
      bar.style.cssText =
        'position:fixed;left:50%;bottom:16px;transform:translateX(-50%);z-index:9999;' +
        'max-width:92%;padding:10px 16px;border-radius:999px;font:600 13px/1.3 system-ui,sans-serif;' +
        'color:#fff;background:rgba(17,17,17,.92);box-shadow:0 6px 24px rgba(0,0,0,.35);' +
        'cursor:pointer;text-align:center;';
      bar.addEventListener('click', function () { bar.remove(); });
      doc.body.appendChild(bar);
      win.setTimeout(function () { if (bar.parentNode) bar.remove(); }, 6000);
    }

    // Park anything already in the DOM, then run setup when the document is ready.
    var existing = doc.querySelectorAll(MANAGED_SELECTOR);
    for (var k = 0; k < existing.length; k++) handle(existing[k]);

    if (doc.readyState === 'loading') {
      doc.addEventListener('DOMContentLoaded', onReady);
    } else {
      onReady();
    }
  }

  return {
    boot: boot,
    // exported for tests
    isSlowEffectiveType: isSlowEffectiveType,
    isDataSaver: isDataSaver,
    isSlowConnection: isSlowConnection,
    decideMode: decideMode,
    park: park,
    activate: activate
  };
});
